import React, { useState, useEffect } from 'react';
import { Upload, Loader2 } from 'lucide-react';

const UploadSection = ({ fileInputRef, analyzing, onFileUpload, progress, shouldSave, onShouldSaveChange, onDemoDataSelect }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [localShouldSave, setLocalShouldSave] = useState(shouldSave !== undefined ? shouldSave : false);

  useEffect(() => {
    if (shouldSave !== undefined) setLocalShouldSave(shouldSave);
  }, [shouldSave]);

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) onFileUpload({ target: { files: [files[0]] } });
  };
  const handleSaveToggle = (save) => {
    setLocalShouldSave(save);
    onShouldSaveChange(save);
    localStorage.setItem('shouldSaveFiles', save.toString());
  };

  return (
    <>
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
        {!analyzing ? (
          <>
            {/* Upload drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
                isDragging
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Upload Your Genetic Data</h3>
              <p className="text-slate-600 mb-4">Drag and drop your file here, or click to browse</p>
              <p className="text-sm text-slate-500">Supports: 23andMe, AncestryDNA, MyHeritage (TXT, CSV)</p>
              <input
                ref={fileInputRef}
                type="file"
                onChange={onFileUpload}
                accept=".txt,.csv,.tsv"
                className="hidden"
              />
            </div>

            {/* Demo Data */}
            <div className="mt-4 flex items-center justify-center gap-3">
              <span className="text-sm text-slate-500">Or try with sample data:</span>
              <select
                onChange={(e) => {
                  if (e.target.value && onDemoDataSelect) {
                    onDemoDataSelect(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white hover:border-indigo-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all cursor-pointer"
                defaultValue=""
              >
                <option value="" disabled>Select demo genome...</option>
                <option value="23andme">Sample Genome (23andMe)</option>
                <option value="ancestry">Sample Genome (AncestryDNA)</option>
              </select>
            </div>

            {/* Save Option */}
            <div className="mt-6 border-t border-slate-200 pt-6 space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-slate-800">Save to Cloud</h4>
                    {localShouldSave && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Enabled
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600">
                    {localShouldSave
                      ? 'Files are compressed (70% smaller) and saved for future access'
                      : 'Results disappear when you close the browser'}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={localShouldSave}
                    onChange={(e) => handleSaveToggle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              {localShouldSave ? (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs">
                  <p className="text-blue-800">
                    <strong>✨ How it works:</strong> Your file is compressed to ~30% of original size, then saved with your analysis results. Load previous files instantly anytime!
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs">
                  <p className="text-amber-800">
                    <strong>🔒 Private mode:</strong> Analysis works normally but results aren't saved. You'll need to re-upload your file each session.
                  </p>
                </div>
              )}
            </div>

            {/* File Format Info */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-600">
                <strong>📁 File Format:</strong> Raw data export from 23andMe, AncestryDNA, or MyHeritage. Should contain SNP data with rsIDs and genotypes.
              </p>
            </div>
          </>
        ) : (
          /* Analysis in Progress */
          <div className="text-center py-12">
            <Loader2 className="w-16 h-16 animate-spin text-indigo-600 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-slate-800 mb-2">
              {progress.stage === 'parsing' && 'Parsing your genetic data...'}
              {progress.stage === 'clinvar' && 'Analyzing clinical variants...'}
              {progress.stage === 'gwas' && 'Cross-referencing GWAS database...'}
              {progress.stage === 'saving' && 'Compressing and saving...'}
              {progress.stage === 'complete' && 'Analysis Complete!'}
            </h3>
            <p className="text-slate-600 mb-6">
              {progress.stage === 'parsing' && 'Reading file and identifying SNPs'}
              {progress.stage === 'clinvar' && `Found ${progress.clinvarCount.toLocaleString()} variants with clinical significance`}
              {progress.stage === 'gwas' && `Processing ${progress.gwasCount.toLocaleString()} GWAS associations`}
              {progress.stage === 'saving' && 'Compressing file to 70% smaller size...'}
              {progress.stage === 'complete' && 'Your results are ready!'}
            </p>
            {progress.stage === 'clinvar' && (
              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">ClinVar Database</span>
                  <span className="text-sm text-slate-600">{progress.clinvarProgress}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                  <div className="bg-indigo-600 h-3 rounded-full transition-all duration-300" style={{ width: `${progress.clinvarProgress}%` }} />
                </div>
              </div>
            )}
            {progress.stage === 'gwas' && (
              <div className="max-w-md mx-auto space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">ClinVar Database</span>
                    <span className="text-sm text-slate-600">100%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-green-600 h-3 rounded-full" style={{ width: '100%' }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">GWAS Catalog</span>
                    <span className="text-sm text-slate-600">{progress.gwasProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div className="bg-purple-600 h-3 rounded-full transition-all duration-300" style={{ width: `${progress.gwasProgress}%` }} />
                  </div>
                </div>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-6">This may take 1–3 minutes depending on your file size</p>
          </div>
        )}
      </div>
    </>
  );
};

export default UploadSection;